import { objectToQueryString } from '@buzzfeed/bf-utils/lib/query-string';
import {
  BackToTop,
  CookieBanner,
  useConsent,
  withErrorBoundary,
} from '@buzzfeed/react-components';
import { captureException } from '@sentry/nextjs';
import Router from 'next/router';
import React, { memo, useEffect, useMemo, useState } from 'react';
import ABeagleProvider from '../../../components/ABeagleProvider';
import Ads from '../../../components/Ads/Ads';
import AdUnit from '../../../components/Ads/AdUnit';
import CommentsManager from '../../../components/CommentsManager';
import ConsentManagement from '../../../components/ConsentManagement';
import { DestinationWrapper } from '../../../components/DestinationWrapper';
import Post from '../../../components/Post';
import CommentsPost from '../../../components/Post/CommentsPost';
import DiscussionPost from '../../../components/Post/DiscussionPost';
import PressBoardAnalytics from '../../../components/PressBoardAnalytics';
import ResizeManager from '../../../components/ResizeManager';
import SiteHeader from '../../../components/SiteHeader';
import StickyManager from '../../../components/StickyManager';
import NewsPageFooter from '../../../components/NewsPageFooter';
import { destinations, site_captcha_key } from '../../../constants';
import { BuzzProvider } from '../../../contexts/buzz';
import { ConsentProvider } from '../../../contexts/consent';
import { useAddressabilityTracking } from '../../../hooks/analytics/useAddressabilityTracking';
import { useBreakingBarTracking } from '../../../hooks/analytics/useBreakingBarTracking';
import { useNavActionTracking } from '../../../hooks/analytics/useNavActionTracking';
import { useNewsBreakingTickerTracking } from '../../../hooks/analytics/useNewsBreakingTickerTracking';
import { usePageviewTracking } from '../../../hooks/analytics/usePageviewTracking';
import { usePageviewCommentsTracking } from '../../../hooks/analytics/usePageviewCommentsTracking';
import { usePerformanceTracking } from '../../../hooks/analytics/usePerformanceTracking';
import { useScrollDepthTracking } from '../../../hooks/analytics/useScrollDepthTracking';
import { useTimeSpentTracking } from '../../../hooks/analytics/useTimeSpentTracking';
import { useCrashReporting } from '../../../hooks/analytics/useCrashReporting';
import { useTaboolaPush } from '../../../hooks/useTaboolaPush';
import { buzzCommonLayerData } from '../../../hooks/analytics/client-event-tracking';
import useTrackonomics from '@buzzfeed/react-components/lib/hooks/useTackonomics';
import { useContentLayoutStability } from '../../../hooks/useContentLayoutStability';
import { useSessionTracking } from '../../../hooks/useSessionTracking';
import { i18n, withTranslation } from '../../../i18n';
import dynamicImport from '../../../utils/dynamic-import';
import { applyUpstreamVaryHeaders, fetchAPI } from '../../../utils/initialProps';
import { isDiscussionPage as _isDiscussionPage } from '../../../utils/isDiscussionPage';
import { useLocalBuzzTracking } from '../../../utils/localBuzzTracking';
import styles from './[slug].module.scss';
import AwarenessAdUnit from '../../../components/Ads/components/AwarenessAd';
import DesktopStickyAd from '../../../components/Ads/components/DesktopStickyAd';
import ToolbarAdUnit from '../../../components/Ads/components/ToolbarAd';
import InterstitialAdUnit from '../../../components/Ads/components/InterstitialAd';
import QuizInterstitialAdUnit from '../../../components/Ads/components/QuizInterstitialAd';
import GameInterstitialAdUnit from '../../../components/Ads/components/GameInterstitialAd';
import { ShoppingHeader } from '../../../components/ShoppingHeader';
import BottomDrawer from '../../../components/BottomDrawer';
import CalloutUnitExperiment from '../../../components/CalloutUnitExperiment';
import { languageFromEdition, normaliseEdition } from '../../../utils/editions';
import { headerBrand } from '../../../utils/brands';
import { isSponsored } from '../../../utils/isSponsored';
import { formatUnixDate } from '../../../utils/dateUtils';
import { isOrHasQuiz } from '../../../utils/isOrHasQuiz';
import AdShieldScript from '../../../components/AdShieldScript';
import KWordsScript from '../../../components/KWordsScript';
import getComments from '../../../api/upstreams/comments-api';
import AdmiralTagScript from '../../../components/AdmiralTagScript';
import useMobileAdHeightVars from '../../../hooks/useMobileAdHeightVars';

const BreakingBar = dynamicImport(
  () =>
    import(
      /* webpackChunkName: "breaking-bar" */ '@buzzfeed/react-components/dist/module/lib/components/BreakingBar'
    ),
  { ssr: false }
);

const NewsBreakingTicker = dynamicImport(
  () =>
    import(
      /* webpackChunkName: "news-breaking-ticker" */ '@buzzfeed/react-components/dist/module/lib/components/NewsBreakingTicker'
    ),
  { ssr: false }
);

// Page view tracking depends on A/B test context in some cases
// Separate this component so it can be nested in ABeagleProvider
// Normally we would want these hooks to live directly in the Page component
const CommonPageLevelTracking = withErrorBoundary(
  ({ buzz }) => {
    useTimeSpentTracking(buzz);
    useScrollDepthTracking(buzz);
    usePerformanceTracking(buzz);
    return null;
  },
  {
    onError: captureException,
  }
);

const BuzzPageLevelTracking = withErrorBoundary(
  ({ buzz }) => {
    useAddressabilityTracking(buzz);
    usePageviewTracking(buzz);
    useLocalBuzzTracking(buzz.id);
    useSessionTracking(buzz.id);
    useTrackonomics(buzz.id);
    return null;
  },
  {
    onError: captureException,
  }
);

const CommentsPageLevelTracking = withErrorBoundary(
  ({ buzz }) => {
    usePageviewCommentsTracking(buzz);
    useAddressabilityTracking(buzz);
    return null;
  },
  {
    onError: captureException,
  }
);

const CrashReporting = withErrorBoundary(
  ({ buzz }) => {
    useCrashReporting(buzz);
    return null;
  },
  {
    onError: captureException,
  }
);

const TaboolaPush = withErrorBoundary(
  ({ buzz }) => {
    useTaboolaPush(buzz);
    return null;
  },
  {
    onError: captureException,
  }
);

export const Page = memo(function Page({
  badges,
  buzz,
  header,
  subbuzzData,
  userEdition,
  userGeo,
  isSmoker,
}) {
  const memoizedBuzz = useMemo(() => buzz, [buzz.id]);
  const memoizedBadges = useMemo(() => badges, [badges]);
  const memoizedSubbuzzData = useMemo(() => subbuzzData, [subbuzzData]);
  const memoizedUserEdition = useMemo(() => userEdition, [userEdition]);

  const isCommentsPage = memoizedBuzz.isCommentsPage;
  const isDiscussionPage = _isDiscussionPage(memoizedBuzz);

  const { consentValue, isConsentKnown, isConsentReady } =
    useConsent('tracking');

  const breakingBarTrackingFn = useBreakingBarTracking(
    memoizedBuzz,
    destinations[buzz.destination_name].base_url
  );

  const newsBreakingTickerFn = useNewsBreakingTickerTracking(memoizedBuzz);
  const [showOverlay, setShowOverlay] = useState(false);

  const buzzEdition = normaliseEdition(memoizedBuzz.classification.edition);

  // always show local edition navigation
  // when both content and user's edition are English
  // see https://buzzfeed.atlassian.net/browse/OOSITE-1189
  const headerEdition = [memoizedUserEdition, buzzEdition]
    .map(languageFromEdition)
    .every(lang => lang === 'en')
    ? userEdition
    : buzzEdition;

  const brand =
    headerBrand(memoizedBuzz?.metavertical) ||
    headerBrand(memoizedBuzz?.classification?.section?.toLowerCase());

  const isNews = memoizedBuzz.destination_name === 'buzzfeed_news';

  // must match the editions bf-header-ui serves the sticky nav to (BF-18867), so the
  // nav is in the sticky registry and the awareness ad sticks below it instead of over it
  const renderHeaderAboveAd = ['us', 'ca'].includes(headerEdition) && !brand && !isNews;

  const membershipAvailable = !memoizedBuzz.disableAds &&
    !isSponsored(memoizedBuzz) &&
    (userGeo === 'US' || userGeo === 'CA');

  useMobileAdHeightVars({ disableAds: !!memoizedBuzz.disableAds });

  const [pageData, setPageData] = useState(null);

  useEffect(() => {
    if (!memoizedBuzz) {
      return;
    }

    const getData = buzzCommonLayerData(memoizedBuzz);
    const data = {
      ...getData(),
      all_sections: memoizedBuzz.all_classifications?.sections || [],
      is_quiz: Boolean(isOrHasQuiz(memoizedBuzz)),
      section: memoizedBuzz.classification ? memoizedBuzz.classification.section : '',
      // @deprecated
      // Remove once the callout API switches to using `section`
      sections: [memoizedBuzz.classification ? memoizedBuzz.classification.section : ''],
    };
    setPageData(data);
  }, [memoizedBuzz]);

  useEffect(() => {
    const onEventMessage = (eventMessage) => {
      if (eventMessage?.data?.type === 'topic-nav-loaded' && membershipAvailable) {
        window.postMessage({ type: 'show-membership-promo-button' }, '*');
      }
    };

    window.addEventListener('message', onEventMessage);

    return () => {
      window.removeEventListener('message', onEventMessage);
    };
  }, []);

  /**
   * @todo
   * Prevent execution if subbuzz data does not include any of the configured content (freeform
   * embeds and tweets). Also, if there are supported subbuzzes and they do not yet have width and
   * height data, increase the sampling rate
   * Does not need to be enabled for comments pages, as subbuzzes do not appear
   */
  useContentLayoutStability({ enabled: !(isCommentsPage || isDiscussionPage) });

  useEffect(() => {
    if (i18n.language !== buzz.language) {
      i18n.changeLanguage(buzz.language);
    }
  }, [buzz.language]);

  const isUsShoppingPost = !isCommentsPage && memoizedBuzz.isShopping && memoizedBuzz.country_code === 'en-us';
  const adUnits = (
    <>
      {!memoizedBuzz.isAd && <AdUnit type="pixel" />}
      {!memoizedBuzz.isAd && <InterstitialAdUnit buzz={memoizedBuzz} />}
      {!memoizedBuzz.isAd && <QuizInterstitialAdUnit buzz={memoizedBuzz} />}
      {!memoizedBuzz.isAd && <GameInterstitialAdUnit />}
      {!memoizedBuzz.isAd && <AwarenessAdUnit buzz={memoizedBuzz} />}
      {!memoizedBuzz.isAd && memoizedBuzz.country_code !== 'ja-jp' && (
        <ToolbarAdUnit buzz={memoizedBuzz}/>
      )}
    </>
  );

  return (
    <>
      <BuzzProvider value={memoizedBuzz}>
        <ConsentProvider
          value={{
            tracking: {
              consentValue,
              isConsentKnown,
              isConsentReady,
            },
          }}
        >
          <ABeagleProvider>
            <DestinationWrapper destination={memoizedBuzz.destination_name}>
              <ConsentManagement isBFN={isNews} isLoadIDNML={false} />
              <CommonPageLevelTracking buzz={memoizedBuzz} />
              <CrashReporting buzz={memoizedBuzz} />
              <TaboolaPush buzz={memoizedBuzz} />
              {isCommentsPage ?
                <CommentsPageLevelTracking buzz={memoizedBuzz} /> :
                <BuzzPageLevelTracking buzz={memoizedBuzz} />}
              <ResizeManager>
                <StickyManager buzz={memoizedBuzz}>
                  {!memoizedBuzz.disableAds && <AdShieldScript />}
                  {!memoizedBuzz.disableAds && <AdmiralTagScript />}
                  <CommentsManager buzz={memoizedBuzz}>
                    {isDiscussionPage && <Ads buzz={memoizedBuzz} badges={memoizedBadges}>
                      {!buzz.isAd && <AdUnit type="pixel" />}
                      {!buzz.isAd && <AwarenessAdUnit buzz={memoizedBuzz}/>}
                      {!buzz.isAd && memoizedBuzz.country_code !== 'ja-jp' && (
                        <AdUnit type="toolbar" />
                      )}
                      {renderHeaderAboveAd && <SiteHeader {...header} renderHeaderAboveAd />}
                      <DiscussionPost
                        buzz={memoizedBuzz}
                        badges={memoizedBadges}
                        subbuzzData={memoizedSubbuzzData}
                      />
                    </Ads>}
                    {!isDiscussionPage && <Ads buzz={memoizedBuzz} badges={memoizedBadges}>
                      {renderHeaderAboveAd && <SiteHeader {...header} renderHeaderAboveAd />}
                      {isUsShoppingPost && (
                        <div className={styles.shoppingAdWrapper}>
                          <div className={styles.shoppingNavWrapper}>
                            <ShoppingHeader
                              setShowOverlay={setShowOverlay}
                              showOverlay={showOverlay}
                              buzz={buzz}
                            />
                          </div>
                          <div className={styles.adUnitsWrapper}>
                            {adUnits}
                          </div>
                        </div>
                      )}
                      {!isUsShoppingPost && adUnits}
                      {!renderHeaderAboveAd && <SiteHeader {...header} renderHeaderAboveAd={false} />}
                      {memoizedBuzz.isNews && (
                        <NewsBreakingTicker track={newsBreakingTickerFn} />
                      )}
                      {(!isCommentsPage && memoizedBuzz.isNews) && (
                        <>
                          <div
                            className={`${
                              memoizedBuzz.longform_custom_header
                                ? styles.breakingBarWrapper
                                : ''
                            } embed-breaking-bar`}
                          >
                            <BreakingBar
                              edition={memoizedBuzz.country_code}
                              track={breakingBarTrackingFn}
                            />
                          </div>
                          {!buzz.isAd && <DesktopStickyAd buzz={memoizedBuzz} />}
                        </>
                      )}
                      {isCommentsPage ?
                        <CommentsPost
                          buzz={memoizedBuzz}
                          badges={memoizedBadges}/> :
                        <Post
                          buzz={memoizedBuzz}
                          subbuzzData={memoizedSubbuzzData}
                          badges={memoizedBadges}
                          showOverlay={showOverlay}
                          setShowOverlay={setShowOverlay}
                        />}
                      {(!isCommentsPage && memoizedBuzz.isNews) && (
                        <NewsPageFooter buzz={memoizedBuzz} />
                      )}
                      {memoizedBuzz.country_code === 'pt-br' && (
                        <CookieBanner />
                      )}
                    </Ads>}
                  </CommentsManager>
                  {!isCommentsPage &&
                    <>
                      <PressBoardAnalytics />
                      <BottomDrawer buzz={memoizedBuzz} />
                    </>}

                  {pageData && !isSmoker &&
                    <CalloutUnitExperiment
                      buzz={memoizedBuzz}
                      pageData={pageData}
                      siteCaptchaKey={site_captcha_key}
                    />}
                  <KWordsScript />
                </StickyManager>
              </ResizeManager>
            </DestinationWrapper>
          </ABeagleProvider>
        </ConsentProvider>
      </BuzzProvider>
      <BackToTop
        className={styles.backToTop}
        track={useNavActionTracking(memoizedBuzz)}
      />
    </>
  );
});

Page.getInitialProps = async ({ res, req, asPath, query = {} }) => {
  const [path] = asPath.split('?');
  const queryParams = req?.query || query;

  // Check for smoker header
  const isSmoker = !!(req?.headers && req.headers['x-bf-smoker']);

  const { error, upstreamResponseHeaders, ...response } = await fetchAPI(
    `${path}.json${objectToQueryString(queryParams)}`,
    { req, includeHeaders: true }
  );

  if (error) {
    return error;
  }

  applyUpstreamVaryHeaders(res, upstreamResponseHeaders);

  const { redirect } = response;
  if (redirect) {
    return res
      ? res.redirect(redirect.status, redirect.location)
      : Router.replace(redirect.location);
  }

  const {
    badges,
    buzz,
    header,
    subbuzzes: subbuzzData,
    userEdition: responseUserEdition,
    userGeo,
  } = response;

  const contentId = buzz.id;
  let topComments = [];
  let commentCount = 0;

  // Only fetch comments on server side (when req object is available)
  if (req) {
    try {
      const commentsData = await getComments({
        contentType: 'buzz',
        contentId: contentId,
        sort: 'hearts',
        pageSize: 10,
        childSize: 0
      });
      topComments = (commentsData.results || []).map(c => ({
        datePublished: formatUnixDate(c.created_at),
        authorName: c.user?.display_name || c.user?.username,
        text: c.blurb,
        upvoteCount: c.love_count,
        downvoteCount: c.flag_count,
      }));
      commentCount = commentsData.count || 0;
    } catch (error) {
      console.error('Error fetching or parsing comments:', error);
      // Proceeding with empty comments, schema will not include them
    }
  }

  if (!req && subbuzzData?.subbuzzes) {
    subbuzzData.subbuzzes = subbuzzData.subbuzzes.map(() => '');
  }

  return {
    namespacesRequired: ['common'],
    badges,
    buzz,
    header,
    subbuzzData,
    userEdition: responseUserEdition,
    userGeo,
    isSmoker,
    topComments, //used in head.jsx
    commentCount, //used in head.jsx
  };
};

export default withTranslation('common')(Page);
